<template>
  <div class="type-podcast">
    <author-toolbar
      v-if="loggedInUser && loggedInUser.capabilities.includes('publish_posts')"
      :post="resource"
    />

    <div class="row">
      <div class="columns small-12 medium-8 large-8 small-order-2 medium-order-1">
        <header>
          <div class="text-podcast subheader">Episode {{ resource.episode }}</div>
          <h1>{{ resource.name }}</h1>

          <nav aria-label="breadcrumbs">
            <ol class="breadcrumbs">
              <li>
                In
                <nuxt-link :to="localePath('/podcasts/')" title="View all Podcasts">
                  Podcasts
                </nuxt-link>
              </li>
            </ol>
          </nav>

          <div class="section-actions flex flex-wrap align-middle">
            <div>
              Recorded
              <time
                pubdate
                :datetime="resource.created"
                :data-tooltip="simpleDate(resource.created, 'dddd, MMM DD, YYYY')"
              >
                {{ relativeTimeFormat(resource.created) }}
              </time>
              by
              <strong rel="author">{{ resource.author.display_name }}</strong>
              <span class="text-mute">|</span>
              Run time: {{ formatDuration }}
            </div>
            <action-buttons class="pull-right" :resource-id="resource.id" />
          </div>
        </header>

        <section class="section section-player">
          <iframe
            ref="podcastPlayer"
            :src="resource.stream_link"
            title="Listen to this Podcast"
            width="100%"
            height="180"
          ></iframe>
        </section>

        <section class="section section-content tw-prose tw-max-w-none">
          <h3>Summary</h3>

          <div v-if="resource.content" class="description" v-html="resource.content"></div>
          <p v-else>
            {{ resource.excerpt }}
          </p>

          <br />
          <nav
            v-if="prevAndNext"
            class="prev-next-nav flex flex-wrap section-prev-and-next"
            aria-label="Previous and next podcasts"
          >
            <nuxt-link
              v-if="prevAndNext.previous"
              :to="localePath(prevAndNext.previous.link)"
              title="View previous lesson"
              class="button button-outlined"
            >
              &larr; {{ prevAndNext.previous.name }}
            </nuxt-link>
            <nuxt-link
              v-if="prevAndNext.next"
              :to="localePath(prevAndNext.next.link)"
              title="View next lesson"
              class="button button-outlined pull-right"
            >
              {{ prevAndNext.next.name }} &rarr;
            </nuxt-link>
          </nav>
        </section>
      </div>

      <aside class="columns small-12 medium-4 large-4 small-order-1 medium-order-2">
        <div>
          <figure v-if="resource.media_thumbnail">
            <responsive-image :image="resource.media_thumbnail" alt="Podcast image" />
          </figure>
          <podcast-links class="hidden-small" :links="subscribe_links" />
        </div>
      </aside>
    </div>

    <section class="row">
      <div class="columns small-12 medium-9 large-9">
        <h3>
          <template v-if="resource.comments && resource.comments > 0">
            {{ resource.comments }}
          </template>
          {{ pluralise('Comment', resource.comments) }}
        </h3>

        <comment-form :resource-id="resource.id" type="comment" layout="minimal" />
        <comment-list
          v-if="resource.comments > 0"
          :resource-id="resource.id"
          :show-load-more-button="$isFlagOn('show-more-comments-btn')"
          :event-params="resource.event_params"
        />
      </div>
    </section>

    <resources-list
      align="center"
      :load-more="true"
      :params="{ id: resource.id }"
      list-type="related"
    />
    <country-switcher :link="resource.link" :linked="resource.linked" :country="resource.country" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ActionButtons from '~/components/resource/ActionButtons.vue';
import PodcastLinks from '~/components/resource/PodcastLinks.vue';
import ResourcesList from '~/components/resource/ResourcesList.vue';
import CommentForm from '~/components/comment/CommentForm.vue';
import ResponsiveImage from '~/components/ResponsiveImage.vue';
import CountrySwitcher from '~/components/CountrySwitcher.vue';
import { pluralise } from '~/utils/formatStrings';
import { relativeTimeFormat, simpleDate } from '~/utils/formatDate';

export default {
  name: 'Podcast',
  components: {
    ActionButtons,
    AuthorToolbar: () => import('~/components/AuthorToolbar.vue'),
    CommentList: () => import('~/components/comment/CommentList.vue'),
    PodcastLinks,
    ResourcesList,
    CommentForm,
    ResponsiveImage,
    CountrySwitcher,
  },
  async asyncData({ app, store, route, params, error }) {
    const data = { types: ['podcast'] };

    try {
      const resource = await store.dispatch('loadResource', {
        id: params.slug,
        types: data.types,
        link: route.path,
        country: app.i18n.locale,
      });

      const prevAndNext = await store.dispatch('loadResourcePrevAndNext', {
        id: resource.id,
        country: app.i18n.locale,
        types: ['podcast'],
        category: 'for-the-love-of-teaching-podcast',
        orderBy: 'created',
      });

      data.prevAndNext = prevAndNext;

      data.title = resource.name || resource.meta_title;
    } catch (err) {
      // No page matching this route
      error({ statusCode: 404, message: err.message, path: route.path });
    }

    return data;
  },
  data() {
    return {
      title: 'Teach Starter Podcast',
      description:
        "Join us as we chat to inspirational, everyday teachers. It's real, it's uplifting, it's funny: it's for the love of teaching!",
      subscribe_links: [
        {
          name: 'Apple Podcasts',
          link: 'https://itunes.apple.com/us/podcast/for-the-love-of-teaching/id1450756371?mt=2&amp;uo=4',
          image: 'icon-podcast-apple.png',
        },
        {
          name: 'Google Podcasts',
          link: 'https://podcasts.google.com/feed/aHR0cHM6Ly93d3cub21ueWNvbnRlbnQuY29tL2QvcGxheWxpc3QvMmZiMzc0MGQtMzQzNi00NGFmLThjYzAtYTkxOTAwNzE2YWE1LzM3ZWQxZjRiLTI0NzktNGM0YS1iZGNjLWFjMGQwMTgyOWM2ZC9mODdmNTM2Ny0wZDA2LTQ5NjUtODEwYS1hYzBkMDE4MjljNmQvcG9kY2FzdC5yc3M',
          image: 'icon-podcast-google.png',
        },
        {
          name: 'Spotify',
          link: 'https://open.spotify.com/show/6tyLl5b4uQJA8oKX1rO32W',
          image: 'icon-podcast-spotify.png',
        },
        { name: 'Pocket Casts', link: 'https://pca.st/4i09', image: 'icon-podcast-pocket.png' },
        {
          name: 'Breaker',
          link: 'https://www.breaker.audio/for-the-love-of-teaching',
          image: 'icon-podcast-breaker.png',
        },
        {
          name: 'Stitcher',
          link: 'https://www.stitcher.com/podcast/anchor-podcasts/for-the-love-of-teaching',
          image: 'icon-podcast-stitcher.png',
        },
      ],
    };
  },
  jsonld() {
    return this.$jsonSchemaResource(this.resource);
  },
  head() {
    return {
      title: this.title,
      meta: [
        ...this.$metaPropertiesResource(this.resource),
        { hid: 'robots', name: 'robots', content: 'noindex' },
      ],
      link: this.$hrefLangsResource(
        this.resource.linked,
        this.resource.link,
        this.resource.country
      ),
    };
  },
  computed: {
    ...mapGetters(['loggedInUser', 'resource']),
    formatDuration() {
      // Convert seconds to minutes and seconds
      if (!this.resource.duration) {
        return '';
      }

      const minutes = Math.floor(this.resource.duration / 60);
      const seconds = this.resource.duration - minutes * 60;

      return seconds === 0 ? minutes + ' min' : minutes + ' min, ' + seconds + ' sec';
    },
  },
  mounted() {
    if (this.resource.event_params) {
      this.$mzAnalyticsApi({
        event: 'pageView',
        name: 'podcast',
        productArea: 'content',
        entityId: this.resource.id,
        entityType: this.resource.type,
      });
    }
  },
  methods: {
    simpleDate,
    relativeTimeFormat,
    pluralise,
  },
};
</script>

<style scoped lang="scss">
@import '~/assets/scss/sections/_resource-page.scss';
</style>
